import React, {
  createContext,
  createRef,
  MutableRefObject,
  ReactChildren,
  ReactFragment,
  ReactPortal,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Group, Object3D } from 'three'
import { GroupProps, RootState, useFrame } from '@react-three/fiber'
import { useScenesStore } from '../../../b.providers/ScenesProvider/store'
import GameElementClass from './scripts/GameElementClass'
import { awaitFor } from '../../../utils/game'
import GameObject from '../classes/GameObject'

type GameElementContextType = {
  uuid?: string
  threeObj?: Object3D
}
export const GameElementContext = createContext<GameElementContextType>({})

export const useUUID = () => {
  const { uuid } = useContext(GameElementContext)
  return uuid
}
export const useObjectInstance = () => {
  const { threeObj } = useContext(GameElementContext)
  return threeObj
}
export type GameElementType = {
  addedToScene?: (obj: Object3D) => void
  onStart?: (obj: Object3D) => void
  onUpdate?: (obj: Object3D, state?: RootState, delta?: number, frame?: any) => void
  onUnload?: (obj: Object3D) => void
  elementClass?: GameObject | any
  name?: string
  children: ReactChildren | ReactFragment | ReactPortal
} & GroupProps
const GameElement = ({
  addedToScene = () => {},
  onStart = () => {},
  onUpdate = () => {},
  onUnload = () => {},
  elementClass = GameObject,
  name,
  children,
  ...props
}: GameElementType) => {
  const elementClassRef = useRef<GameObject>()
  const [uuid, setUuid] = useState<string>()
  const [threeObj, setThis] = useState<Object3D>()
  const getGameElements = useScenesStore(s => s.getGameElements)
  const addGameElement = useScenesStore(s => s.addGameElement)

  const handleElementAddedToScene = async (obj: Object3D): Promise<MutableRefObject<any> | Record<string, unknown>> => {
    const key = name || obj?.uuid
    const isNew = obj && !getGameElements().has(key)
    const isUpdate = obj && getGameElements().has(key) && obj !== getGameElements().get(key)
    if (isNew || isUpdate) {
      obj.userData.key = key
      elementClassRef.current = new elementClass(obj)
      // stores reference at module level into GameProvider store
      addGameElement(key, obj)
      // sets local uuid to provide in context
      setUuid(key)
      // local reference of the object
      // Use factory to create extensions of Object3D
      setThis(obj)

      // await for children to be rendered
      await awaitFor({
        compare: () => {
          return obj.children.length > 0
        },
      })
      // triggers prop callback
      addedToScene(obj)
      // life cycle callback
      onStart(obj)

      elementClassRef.current?.onStart()
    }
    const aRef = createRef<Object3D>()
    return aRef
  }

  useFrame((state, delta, frame) => {
    if (threeObj) {
      onUpdate(threeObj, state, delta, frame)
      elementClassRef.current?.onUpdate(state, delta, frame)
    }
  })

  // life cycle callback
  useEffect(() => {
    // ---
    // anything called here is a pre mount since the Object3d is not available yet
    // ---
    return () => {
      if (threeObj) {
        onUnload(threeObj)
        elementClassRef.current?.onUnload()
      }
    }
  }, [threeObj])

  // an attempt to "memoize" children and avoid re-render them
  const [storedChildren, setStoredChildren] = useState<any>()
  useEffect(() => {
    if (uuid && children /*&& !storedChildren*/) {
      setStoredChildren(children)
    }
  }, [uuid, children, storedChildren])
  const childrenToRender = uuid ? storedChildren ?? null : null
  // ----------------------------------------------------------
  return (
    <GameElementContext.Provider value={{ uuid, threeObj }}>
      <group
        ref={handleElementAddedToScene as Ref<Group>}
        {...props}
        userData={{ isGameElement: true, ...(props.userData || {}), ...(threeObj?.userData || {}) }}
      >
        {uuid && children}
      </group>
    </GameElementContext.Provider>
  )
}

export default GameElement
